import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';

import { generateApiPath, getCollectionPaginated } from '#app/Handler/api';
import OrderClientEntryPoints from '#app/entrypoints/orderClient';
import { Collection } from '#app/typings/api';
import { OrderClientList, OrderClientState } from '#app/typings/orderClient';
import { Pagination, Sorting } from '#app/typings/utils';

type HookReturn = {
  clients: OrderClientList[];
  totalItems: number;
  loading: boolean;
  pagination: Pagination;
  sorting: Sorting;
  setPagination: (pagination: Pagination) => void;
  setSorting: (sorting: Sorting) => void;
  setFilters: (filters: Filters) => void;
};

export type Filters = {
  number?: string;
  email?: string;
  name?: string;
  orderClientStates?: OrderClientState[];
};

const useOrderClientList = (): HookReturn => {
  const { shopId } = useParams();
  const [filters, setFilters] = useState<Filters>({});
  const [pagination, setPagination] = useState<Pagination>({ page: 0, limit: 25 });
  const [sorting, setSorting] = useState<Sorting>({ sortName: 'createdAt', sortDirection: 'desc' });
  const { data: clients, isLoading } = useSWR<Collection<OrderClientList>>(
    generateApiPath(
      OrderClientEntryPoints.getCollectionByShop,
      { shopId: shopId as string },
      { ...pagination, ...sorting, ...filters },
    ),
    getCollectionPaginated,
    {
      keepPreviousData: true,
      revalidateOnFocus: pagination.page === 0,
    },
  );

  return {
    clients: clients?.data ?? [],
    totalItems: clients?.totalItems ?? 0,
    loading: isLoading,
    pagination,
    setPagination: (pagination) => setPagination(pagination),
    sorting,
    setSorting: (sorting) => setSorting(sorting),
    setFilters: (filters) => setFilters(filters),
  };
};

export default useOrderClientList;
